import React from 'react';
import useScheduleData from './useScheduleData';
import DateLocationBar from './DateLocationBar';
import PendingAllocationStrip from './PendingAllocationStrip';
import ScheduleGrid from './ScheduleGrid';
import { Loader2, AlertCircle } from 'lucide-react';

export default function DashboardShell() {
  const {
    locations,
    locationId,
    setLocationId,
    date,
    setDate,
    goToday,
    shiftDay,
    schedule,
    loading,
    error,
    refresh,
  } = useScheduleData();

  return (
    <div data-testid="admin-dashboard" className="space-y-3">
      <div className="flex items-baseline justify-between print:hidden">
        <h1 className="text-2xl lg:text-3xl font-black font-heading uppercase">
          Dashboard
        </h1>
        {schedule && (
          <p className="text-xs text-muted-foreground" data-testid="schedule-day-label">
            {schedule.day_of_week}, {schedule.date}
          </p>
        )}
      </div>

      {/* Print-only header for the printed schedule */}
      <div className="hidden print:block">
        <h1 className="text-2xl font-bold">{schedule?.location?.name}</h1>
        <p className="text-sm">
          {schedule?.day_of_week}, {schedule?.date}
          {schedule?.holiday ? ` — Holiday: ${schedule.holiday.name}` : ''}
          {schedule?.is_friday_prayer ? ' — Friday Prayer block in effect' : ''}
        </p>
      </div>

      <DateLocationBar
        locations={locations}
        locationId={locationId}
        setLocationId={setLocationId}
        date={date}
        setDate={setDate}
        goToday={goToday}
        shiftDay={shiftDay}
        refresh={refresh}
        loading={loading}
      />

      {error && (
        <div
          className="border-2 border-red-300 bg-red-50 text-red-900 rounded-2xl px-3 py-2 flex items-center gap-2"
          data-testid="dashboard-error"
        >
          <AlertCircle className="w-4 h-4" />
          <p className="text-xs">{error}</p>
        </div>
      )}

      <PendingAllocationStrip
        items={schedule?.pending_allocation_bookings || []}
        schedule={schedule}
        locationId={locationId}
        date={date}
      />

      {schedule?.holiday && (
        <div
          className="border-2 border-red-300 bg-red-50 text-red-900 rounded-2xl px-3 py-2 text-xs"
          data-testid="dashboard-holiday-banner"
        >
          🎉 <span className="font-bold">{schedule.holiday.name}</span>{' '}
          {schedule.holiday.type ? `(${schedule.holiday.type} holiday)` : ''}
        </div>
      )}
      {schedule?.is_friday_prayer && (
        <div
          className="border-2 border-blue-300 bg-blue-50 text-blue-900 rounded-2xl px-3 py-2 text-xs"
          data-testid="dashboard-friday-banner"
        >
          🕌 Friday — prayer block hours apply per location settings (12:00–14:00 typical).
        </div>
      )}

      {loading && !schedule ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading schedule…
        </div>
      ) : (
        <ScheduleGrid schedule={schedule} />
      )}

      {schedule && schedule.assigned_bookings.length === 0 && (
        <p
          className="text-center text-xs text-muted-foreground py-1 print:hidden"
          data-testid="dashboard-empty-hint"
        >
          No assigned bookings for this date / location yet.
        </p>
      )}
    </div>
  );
}
